/**
 * Durable storage for a captured referral / invite code.
 *
 * A code is captured from the landing URL (?ref= or ?code=) and must outlive
 * client-side navigation AND the eventual social-login OAuth round-trip. We
 * persist in two places on purpose:
 *
 *   - localStorage : primary store; carries metadata (capturedAt) so we can
 *                    enforce the attribution window. Survives a same-origin
 *                    redirect (the OAuth callback returns to our own domain).
 *   - cookie       : mirror scoped to the registrable domain (.avrah.it) so the
 *                    code is still readable if a future OAuth callback ever
 *                    lands on a different subdomain than where it was captured,
 *                    and server-side if we ever want it there.
 *
 * Read prefers localStorage (richer); falls back to the cookie. Both are cleared
 * together once the code has been consumed.
 */

const STORAGE_KEY = "avrah.referral";
const COOKIE_NAME = "avrah_ref";

// Attribution window: a captured code applies for this long, then it is dropped
// so a stale link can't silently extend a trial weeks later.
const TTL_DAYS = 30;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

// On *.avrah.it use a domain-scoped cookie so all subdomains share it. On
// localhost / preview hosts, omit the domain attribute (a Domain that doesn't
// match the host is silently rejected, which would drop the cookie entirely).
function cookieDomain() {
  const host = (typeof window !== "undefined" && window.location.hostname) || "";
  return host === "avrah.it" || host.endsWith(".avrah.it") ? ".avrah.it" : null;
}

function writeCookie(value) {
  try {
    const maxAge = Math.floor(TTL_MS / 1000);
    const domain = cookieDomain();
    const domainAttr = domain ? `; Domain=${domain}` : "";
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${COOKIE_NAME}=${encodeURIComponent(value)}; Max-Age=${maxAge}` +
      `; Path=/; SameSite=Lax${domainAttr}${secure}`;
  } catch (e) {
    // Cookies blocked/disabled: localStorage still carries the code.
    console.warn("[referral] writeCookie failed:", e);
  }
}

function readCookie() {
  try {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${COOKIE_NAME}=`));
    return match
      ? decodeURIComponent(match.slice(COOKIE_NAME.length + 1))
      : null;
  } catch (e) {
    console.warn("[referral] readCookie failed:", e);
    return null;
  }
}

function clearCookie() {
  try {
    const domain = cookieDomain();
    const domainAttr = domain ? `; Domain=${domain}` : "";
    document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${domainAttr}`;
  } catch (e) {
    console.warn("[referral] clearCookie failed:", e);
  }
}

/**
 * Persist a captured referral code to localStorage + cookie. A blank/falsy code
 * clears any stored value instead of writing an empty one.
 */
export function saveReferral(code, source = "url") {
  const trimmed = (code || "").trim();
  if (!trimmed) {
    clearReferral();
    return;
  }
  const record = { code: trimmed, capturedAt: Date.now(), source };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    // localStorage unavailable (private mode / quota): the cookie still holds it.
    console.warn(
      `[referral] saveReferral localStorage write failed for "${trimmed}":`,
      e
    );
  }
  writeCookie(trimmed);
}

/**
 * Read the persisted referral code, or null if none / expired. Expired records
 * are cleared on read so they can never resurface.
 */
export function readReferral() {
  let record = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) record = JSON.parse(raw);
  } catch (e) {
    console.warn(
      "[referral] readReferral localStorage parse failed; ignoring stored record:",
      e
    );
    record = null;
  }

  if (record && record.code) {
    if (Date.now() - (record.capturedAt || 0) > TTL_MS) {
      clearReferral();
      return null;
    }
    return record.code;
  }

  // localStorage empty (or wiped by a cross-subdomain hop): fall back to the
  // cookie. It has no timestamp, but its own Max-Age enforces the same window.
  const cookieCode = readCookie();
  return cookieCode && cookieCode.trim() ? cookieCode.trim() : null;
}

/** Remove the persisted code from both stores. */
export function clearReferral() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn("[referral] clearReferral localStorage remove failed:", e);
  }
  clearCookie();
}

export const REFERRAL_TTL_DAYS = TTL_DAYS;
